import { environment } from 'src/environments/environment';
import { FLOATING_TYPES } from './floating-types';
import { UnitConstants } from './unit';

export class CONSTANTS {
  public static readonly APP_TITLE =
    environment.datasheet || !environment.anflexWeb ? 'RiserDatasheet' : 'AnflexWeb';

  public static readonly STORAGE = {
    USER: 'user',
    ACCESS_TOKEN: 'access_token',
    REFRESH_TOKEN: 'refresh_token',
    SIMULATION: 'currentSimulation',
    PERMISSIONS: 'currentPermissions',
  };

  public static readonly MINIMUM_AFFIX = 'Minimum';
  public static readonly MAXIMUM_AFFIX = 'Maximum';
  public static readonly MOST_PROBABLE_AFFIX = 'MostProbable';

  public static readonly FIELD_TYPES = {
    INPUT: 'input',
    TEXTAREA: 'textarea',
    AUTOCOMPLETE: 'autocomplete',
    SELECT: 'select',
    CHECKBOX: 'checkbox',
  };

  public static readonly SELECT_VALUE_ELEMENT = '.ng-value-label';

  public static readonly TITLES = {
    PROPERTIES_DATA: 'Enter properties data',
    SOIL: 'Enter soil geometry data',
    FLUID_PARAMETERS: 'Fluid parameters',
    SELECT_ACCESSORIES: 'Select accessories',
    CATENARY_VIEW: 'Preview of riser geometry',
    CATENARY_ERROR: 'The simulation does not satisfy the minimum requirements to build a catenary',
    LINE: 'Line data',
    SEGMENTS: 'Segments',
  };

  public static readonly TERMS = {
    GAMMA: '&#x03B3;',
  };

  public static readonly MODAL_ID = {
    DELETE_DATASHEET: 'datasheetDeleteModal',
    FLUID_PARAMETERS: 'fluidParameters',
    SELECT_ACCESSORIES: 'selectAccessories',
    CANCEL_PROJECT: 'cancelProject',
    CANCEL_LINE: 'cancelLineConfig',
    DATASHEET_METADATA: 'datasheetMetadataModal',
    DATASHEET_FILTER: 'datasheetFilter',
    SHARE: 'shareModal',
    DELETE_USER: 'userDeleteModal',
    UPSERT_USER: 'userUpsertModal',
    LINE_IMPORT: 'lineImport',
    DELETE_CONFIRMATION: 'deleteConfirmationModal',
    CREATE_PROCESS: 'createProcessModal',
    PROCESS_LOG: 'processLogModal',
    ANALYSIS_FLUID: 'analysisFluid',
  };

  public static readonly STEPS = {
    GLOBAL: 'GLOBAL',
    SOILS: 'SOILS',
    PIPES: 'PIPES',
    ACCESSORIES: 'ACCESSORIES',
    LINE: 'LINE',
  };

  public static readonly GLOBAL = 'global';
  public static readonly SOIL = 'soil';
  public static readonly LINE = 'line';
  public static readonly LINES = 'lines';
  public static readonly FUNCTIONS = 'functions';
  public static readonly HARMONIC_FUNCTION = 'harmonicFunctions';
  public static readonly ROTATIONAL_FUNCTION = 'rotationalFunction';
  public static readonly FLUID_FUNCTION = 'fluidFunction';
  public static readonly SN_CURVE = 'snCurve';
  public static readonly FATIGUE = 'fatigue';
  public static readonly WAVES = 'waves';
  public static readonly WAVE_GROUPS = 'waveGroups';
  public static readonly CURRENT_GROUPS = 'currentGroups';
  public static readonly LINE_RESULTS_REQUESTERS = 'lineResultsRequesters';
  public static readonly NODE = 'node';
  public static readonly NODE_RESTRAINTS = 'nodeRestraints';
  public static readonly NODE_STIFFNESS = 'nodeStiffness';
  public static readonly ANALYSIS_CASE = 'analysisCase';
  public static readonly REFERENCE_SYSTEM = 'referenceSystem';
  public static readonly GRAPHIC_PROPERTIES = 'graphicProperties';
  public static readonly INTERNAL_ID = 'internalId';
  public static readonly NEEDS_UPDATE = 'needsUpdate';

  public static readonly ACCESSORY_TYPES = {
    FLOATER: 'floater',
    STIFFENER: 'stiffener',
    STRAKE: 'strake',
    FLEXJOINT: 'matFlexJoint',
  };

  public static readonly MATERIAL_TYPES = {
    FLEX_LINE: 'matFlexLine',
    CONNECTOR: 'matConnector',
    RIGID_TUBE: 'matRigidTube',
    STRESS_JOINT: 'matStressJoint',
    CABLE: 'matCable',
  };

  public static readonly LINE_BINDING_TYPES = {
    FLOATING: 'floating',
    CONNECTION: 'connection',
  };

  public static readonly RESULT_ENTITIES = {
    LINE_RESULTS_REQUESTER: 'LineResultsRequester',
  };

  public static readonly BUOYANCY = 'BUOYANCY';
  public static readonly MODULE = 'MODULE';
  public static readonly STIFFENER = 'STIFFENER';
  public static readonly STRAKE = 'STRAKE';
  public static readonly FLEX_JOINT = 'FLEX JOINT';

  public static readonly NONE = 'None';
  public static readonly NONE_LOWER = 'none';
  public static readonly NO = 'NO';
  public static readonly YES = 'YES';

  public static readonly EMPTY_OPTION = {
    internalId: -1,
    title: CONSTANTS.NONE,
  };

  public static readonly BOOLEAN_OPTIONS = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  public static readonly CARDINAL_POINTS = {
    N: 0,
    NEE: 22.5,
    NE: 45,
    ENE: 67.5,
    E: 90,
    ESE: 112.5,
    SE: 135,
    SSE: 157.5,
    S: 180,
    SSW: 202.5,
    SW: 225,
    WSW: 247.5,
    W: 270,
    WNW: 292.5,
    NW: 315,
    NNW: 337.5,
  };

  public static readonly LINE_BOUNDARY_IGNORE_DIVERGENCY_FACTOR = 10e-4;

  public static readonly SUMMARY_TABLE_COLUMNS = {
    HORIZONTAL_PROJECTION: {
      LABEL: 'Horizontal projection',
      UNIT: `(${UnitConstants.METERS})`,
    },
    VERTICAL_PROJECTION: {
      LABEL: 'Vertical projection',
      UNIT: `(${UnitConstants.METERS})`,
    },
    MINIMUM_CURVATURE: {
      LABEL: 'Minimum curvature radius',
      UNIT: `(${UnitConstants.METERS})`,
    },
    TDP_FROM_CONNECTION: {
      LABEL: 'Tdp from connection',
      UNIT: `(${UnitConstants.METERS})`,
    },
    TDP_FROM_ANCHOR: {
      LABEL: 'Tdp from anchor',
      UNIT: `(${UnitConstants.METERS})`,
    },
    TOTAL_LENGTH: {
      LABEL: 'Total length',
      UNIT: `(${UnitConstants.METERS})`,
    },
    SUSPENDED_LENGTH: {
      LABEL: 'Suspended length',
      UNIT: `(${UnitConstants.METERS})`,
    },
    ANGLE_AT_ANCHOR: {
      LABEL: 'Angle at anchor W.R.T sea surface',
      UNIT: `(${UnitConstants.DEGREE})`,
    },
    HORIZONTAL_FORCE_AT_ANCHOR: {
      LABEL: 'Horizontal force at anchor',
      UNIT: `(${UnitConstants.K_NEWTONS})`,
    },
    VERTICAL_FORCE_AT_ANCHOR: {
      LABEL: 'Vertical force at anchor',
      UNIT: `(${UnitConstants.K_NEWTONS})`,
    },
    TOTAL_FORCE_AT_ANCHOR: {
      LABEL: 'Total force at anchor',
      UNIT: `(${UnitConstants.K_NEWTONS})`,
    },
    ANGLE_AT_CONN_SEA_S: {
      LABEL: 'Angle at conn. W.R.T. sea surface',
      UNIT: `(${UnitConstants.DEGREE})`,
    },
    ANGLE_AT_CONN: {
      LABEL: 'Angle at conn. W.R.T. vertical',
      UNIT: `(${UnitConstants.DEGREE})`,
    },
    HORIZONTAL_FORCE_AT_CONNECTION: {
      LABEL: 'Horizontal force at connection',
      UNIT: `(${UnitConstants.K_NEWTONS})`,
    },
    VERTICAL_FORCE_AT_CONNECTION: {
      LABEL: 'Vertical force at connection',
      UNIT: `(${UnitConstants.K_NEWTONS})`,
    },
    TOTAL_FORCE_AT_CONN: {
      LABEL: 'Total force at connection',
      UNIT: `(${UnitConstants.K_NEWTONS})`,
    },
    HOG_SEABED_DIST: {
      LABEL: 'Hog seabed dist',
      UNIT: `(${UnitConstants.METERS})`,
    },
    HOG_HORIZONTAL_DIST: {
      LABEL: 'Hog horizontal dist',
      UNIT: `(${UnitConstants.METERS})`,
    },
    SAG_SEABED_DIST: {
      LABEL: 'Sag seabed dist',
      UNIT: `(${UnitConstants.METERS})`,
    },
    SAG_HORIZONTAL_DIST: {
      LABEL: 'Sag horizontal dist',
      UNIT: `(${UnitConstants.METERS})`,
    },
  };

  public static readonly JSON_DOWNLOAD_URI_PREFIX = 'data:text/json;charset=UTF-8,';
  public static readonly APP_JSON_TYPE = 'application/json';
  public static readonly MULTIPART_FORM_DATA = 'multipart/form-data';
  public static readonly AML_UPLOAD_EXT = '.aml';
  public static readonly JSON_UPLOAD_EXT = '.json';
  public static readonly RAO_UPLOAD_EXT = '.rao';
  public static readonly COF_UPLOAD_EXT = '.cof';

  public static readonly SEGMENT_ORIENTATION_TYPES = {
    NONE: 'no',
    TOWARDS_BOTTOM: 'bottom',
    TOWARDS_TOP: 'up',
  };

  public static readonly SUMMARY_INFO_HEADER_TITLES = ['fluid(min)', 'fluid(mp)', 'fluid(max)'];

  public static readonly SELECT_PLACEHOLDER = 'Select...';
  public static readonly SEG_CONFIG = 'segConfig';
  public static readonly FLOATER_CONFIG = 'floaterConfig';
  public static readonly STRAKE_CONFIG = 'strakeConfig';
  public static readonly STIFFENER_BEG_ID = 'stiffenerBeginId';
  public static readonly STIFFENER_END_ID = 'stiffenerEndId';
  public static readonly FLEXJOINT_BEG_ID = 'flexjointBeginId';
  public static readonly FLEXJOINT_END_ID = 'flexjointEndId';
  public static readonly BEG_ACCESSORY_TYPE = 'beginningAccessoryType';
  public static readonly END_ACCESSORY_TYPE = 'endAccessoryType';
  public static readonly SEGMENT_MATERIAL = 'segmentMaterial';
  public static readonly SEGMENT_SOIL = 'segmentSoil';
  public static readonly FLOATER_ENABLED = 'floaterEnabled';
  public static readonly STIFFENER_ENABLED = 'stiffenerEnabled';
  public static readonly STRAKE_ENABLED = 'strakeEnabled';
  public static readonly FLEXJOINT_ENABLED = 'flexJointEnabled';
  public static readonly ACC_ID = 'accId';
  public static readonly START = 'start';
  public static readonly END = 'end';
  public static readonly SEGMENTS_TABLE_ROWS = 'segmentsTableRows';
  public static readonly MESH_TABLE_ROWS = 'meshTableRows';
  public static readonly NUMBER_OF_WAVES = 'numberOfWaves';
  public static readonly NUMBER_OF_CURRENTS = 'numberOfCurrents';
  public static readonly NUMBER_OF_POINTS = 'numberOfPoints';
  public static readonly NUMBER_OF_ROWS = 'numberOfRows';
  public static readonly NUMBER_OF_SEGMENT = 'numberOfSegment';
  public static readonly INTERNAL_DIAMETER = 'internalDiameter';
  public static readonly EXTERNAL_DIAMETER = 'externalDiameter';
  public static readonly EFFECTIVE_DIAMETER = 'diameterEffective';
  public static readonly CATENARY_PROJECTION = 'projection';
  public static readonly CATENARY_ANGLE = 'angle';
  public static readonly SEG_NUMBER = 'segNumber';
  public static readonly MATERIAL_REFERENCE = 'materialReference';
  public static readonly LENGTH = 'length';
  public static readonly ROW_NUMBER = 'rowNumber';
  public static readonly SEGMENT = 'segment';
  public static readonly DI = 'di';
  public static readonly DF = 'df';
  public static readonly LABEL = 'label';
  public static readonly POSITION = 'position';
  public static readonly TITLE = 'title';
  public static readonly BEGINNING = 'beginning';
  public static readonly SAMPLING = 'sampling';
  public static readonly SAMPLE = 'sample';
  public static readonly INTERVALS = 'intervals';
  public static readonly CONNECTORS = 'connectors';
  public static readonly STATIC_ENVELOPE_INTERVAL_INIT = 'staticEnvelopeIntervalInit';
  public static readonly STATIC_ENVELOPE_INTERVAL_END = 'staticEnvelopeIntervalEnd';
  public static readonly DYNAMIC_ENVELOPE_INTERVAL_INIT = 'dynamicEnvelopeIntervalInit';
  public static readonly DYNAMIC_ENVELOPE_INTERVAL_END = 'dynamicEnvelopeIntervalEnd';
  public static readonly STEEL_REGIONS = 'steelRegions';
  public static readonly FLEX_REGIONS = 'flexRegions';

  public static readonly PRESERVE_ASPECT_RATIO = 'preserveAspectRatio';
  public static readonly SVG = 'svg';
  public static readonly DEFS = 'defs';
  public static readonly SVG_MID = 'xMidYMid meet';
  public static readonly SVG_MIN = 'xMinYMin meet';
  public static readonly VIEW_BOX = 'viewBox';
  public static readonly FILL = 'fill';
  public static readonly STROKE = 'stroke';
  public static readonly STROKE_WIDTH = 'stroke-width';
  public static readonly PATH = 'path';
  public static readonly WIDTH = 'width';
  public static readonly HEIGHT = 'height';
  public static readonly VISIBILITY = 'visibility';
  public static readonly VISIBLE = 'visible';
  public static readonly HIDDEN = 'hidden';
  public static readonly MASK = 'mask';
  public static readonly RECT = 'rect';
  public static readonly ATTR = 'attr';
  public static readonly STYLE = 'style';
  public static readonly TEXT = 'text';
  public static readonly TRANSFORM = 'transform';

  public static readonly ACCESSORY_GROUP = 'accessory-group';
  public static readonly DIVISIONS_GROUP = 'catenary-divisions-group';
  public static readonly CATENARY_GROUP = 'catenary-line-group';
  public static readonly CATENARY_LINE = 'catenary-line';
  public static readonly CATENARY_HEATMAP = 'catenary-heatmap';
  public static readonly LINEAR_GRADIENT = 'linearGradient';
  public static readonly LINE_GRADIENT = 'line-gradient';
  public static readonly ID = 'id';
  public static readonly GRADIENT_UNITS = 'gradientUnits';
  public static readonly USER_SPACE_ON_USE = 'userSpaceOnUse';
  public static readonly X1 = 'x1';
  public static readonly Y1 = 'y1';
  public static readonly X2 = 'x2';
  public static readonly Y2 = 'y2';
  public static readonly STOP = 'stop';
  public static readonly OFFSET = 'offset';
  public static readonly STOP_COLOR = 'stop-color';
  public static readonly MOUSEOVER = 'mouseover';
  public static readonly MOUSEOUT = 'mouseout';
  public static readonly FONT_SIZE = 'font-size';

  public static readonly VERSION = 'Version';
  public static readonly REVISIONS = 'Revisions';

  public static readonly CATENARY_DEBOUNCE = 350;
  public static readonly ERRORS_LOG_DEBOUNCE = 500;
  public static readonly LOCAL_SAVE_DEBOUNCE = 500;
  public static readonly AUTO_SAVE_DEBOUNCE = 1000;

  public static readonly DATE_FORMAT = {
    FRONT: 'MM/DD/yyyy',
    API: 'YYYY-MM-DD',
  };
  public static readonly INVALID_DATE = 'Invalid date';

  public static readonly FLOATING_TYPES = [
    {
      name: 'ship',
      value: FLOATING_TYPES.SHIP,
    },
    {
      name: 'plataform',
      value: FLOATING_TYPES.PLATFORM,
    },
  ];

  public static readonly MOORING_TYPES = [
    {
      name: 'spread',
      value: 'SPREAD',
    },
    {
      name: 'turret',
      value: 'TURRET',
    },
  ];

  public static readonly CHART_TYPES = {
    MAIN_ENVIRONMENT: 'main',
    SEGMENTS: 'segments',
    SIMPLE_PLOT: 'simple_plot',
  };

  public static readonly RAO_DATA = {
    DOFS: ['SURGE', 'SWAY', 'HEAVE', 'ROLL', 'PITCH', 'YAW'],
    COMPONENTS: [
      { name: 'AMPLITUDE', value: 'Amp' },
      { name: 'PHASE', value: 'Phase' },
    ],
    PLOT_TYPES: {
      COMPLEX: 'COMPLEX',
      AMPLITUDES: 'AMPLITUDES',
    },
    X_AXIS_OPTIONS: {
      FREQUENCY: 'FREQUENCY',
      PERIOD: 'PERIOD',
    },
  };

  public static readonly SIMPLE_PLOT_PARAMS = {
    WIDTH: 1000,
    HEIGHT: 1000,
    POINTS_HELPER_WIDTH: 150,
    POINTS_HELPER_HEIGHT: 50,
    LEGEND_WIDTH: 200,
    MARGIN: { top: 50, right: 20, bottom: 50, left: 90 },
    DIVISION_SCALING_DENOMINATOR: 100,
    TOOLTIP_SCALING_DENOMINATOR: 30,
    CURVE_COLOR_PATTERN: [
      '#ffff99',
      '#a6cee3',
      '#b2df8a',
      '#fb9a99',
      '#fdbf6f',
      '#cab2d6',
      '#0098ff',
      '#33a02c',
      '#ff7f00',
    ],
    NUM_DECIMAL_HOUSES: 4,
  };

  public static readonly MESH_HEATMAP_COLORS = {
    LOW_INTENSITY: '#66CC00',
    HIGH_INTENSITY: '#ff5c21',
  };

  public static readonly KEY_HOLD_EVENT = 'holding';
  public static readonly DOUBLE_CLICK_EVENT = 'dblclick';

  public static readonly MAIN_CHART_TYPES = {
    CHART_2D: '2D',
    CHART_3D: '3D',
  };

  public static readonly REGION = 'region';
  public static readonly CONNECTOR = 'connector';

  public static readonly CLIPBOARD_TEXT = 'Text';
  public static readonly ROW_TAG = 'tr';

  public static readonly MOUSE_EVENT = {
    UP: 'mouseup',
    DOWN: 'mousedown',
    OVER: 'mouseover',
    LEFT: 0,
    RIGHT: 2,
  };

  public static readonly UNTITLED_OBJECT = 'Untitled object';
  public static readonly FIRST_PEAK_TABLE = 'firstPeakTable';
  public static readonly SECOND_PEAK_TABLE = 'secondPeakTable';
  public static readonly SECONDE_PEAK_FIELDS_AFFIX = 'Swell';
  public static readonly SUBTITLE_REGULAR = 'Regular Waves';
  public static readonly SUBTITLE_IRREGULAR = 'Irregular Waves';
  public static readonly CURRENTS = 'currents';
  public static readonly POINTS = 'points';
  public static readonly POINTS_TABLE = 'pointsTable';
  public static readonly ORIENTED_BY_FIELD_NAME = 'orientedBy';
  public static readonly ORIENTATION_HANDLER_NAME = 'orientationHandler';
  public static readonly SECOND_PEAK = 'secondPeak';
  public static readonly MODEL_FIELD_NAME = 'model';
  public static readonly CARDINAL_POINT_FIELD_NAME = 'cardinalPoint';
  public static readonly AZIMUTH_BR_FIELD_NAME = 'azimuthBR';
  public static readonly AZIMUTH_FIELD_NAME = 'azimuth';
  public static readonly ANGLE_FIELD_NAME = 'angle';
  public static readonly ANCHOR_AFFIX = '_anchor';
  public static readonly SEG_AFFIX = '_webSeg';
  public static readonly LOADING_CASES_TABLE = 'loadingCasesTable';
  public static readonly STATIC_CONFIG = 'staticConfig';
  public static readonly DYNAMIC_CONFIG = 'dynamicConfig';
  public static readonly ASSEMBLY_CONFIG = 'assemblyConfig';
  public static readonly HARMONIC_TABLE = 'harmonicTable';

  public static readonly MULTIPLE = '(Multiple)';
  public static readonly ALL_STRUCTURES = 'All structures';
  public static readonly ALL_LAYERS = 'All Layers';

  public static readonly NO_PROGRESS = 'No progress available';
  public static readonly NOT_FOUND = 'Not found';
  public static readonly NOT_STARTED = 'Not started';
  public static readonly SUBMITTED = 'Submitted';

  public static readonly EXPORTABLE_SHEET_CONFIGS = {
    MIN_VAR_CONDITION: { font: { color: { rgb: 'FF0000' } } },
    MAX_VAR_CONDITION: { font: { color: { rgb: '00FF00' } } },
  };

  public static readonly FLOAT_POINT_ERROR_MODULE = 1e-9;

  public static readonly CATENARY_REGION_COLORS = [
    '#72bcb4',
    '#66cc00',
    '#c40000',
    '#ff5c21',
    '#9bcaaa',
    '#8a9dc7',
    '#f5d687',
    '#ea9b57',
    '#c37da6',
  ];
  public static readonly COLORS = [
    'rgb(179, 205, 227)',
    'rgb(204, 235, 197)',
    'rgb(222, 203, 228)',
    'rgb(254, 217, 166)',
    'rgb(255, 255, 204)',
    'rgb(229, 216, 189)',
    'rgb(253, 218, 236)',
    'rgb(242, 242, 242)',
    'rgb(179, 226, 205)',
    'rgb(253, 205, 172)',
    'rgb(203, 213, 232)',
    'rgb(244, 202, 228)',
    'rgb(230, 245, 201)',
    'rgb(255, 242, 174)',
    'rgb(241, 226, 204)',
    'rgb(204, 204, 204)',
  ];
  public static readonly DIMMED_COLORS = [
    'rgba(179, 205, 227, 0.6)',
    'rgba(204, 235, 197, 0.6)',
    'rgba(222, 203, 228, 0.6)',
    'rgba(254, 217, 166, 0.6)',
    'rgba(255, 255, 204, 0.6)',
    'rgba(229, 216, 189, 0.6)',
    'rgba(253, 218, 236, 0.6)',
    'rgba(242, 242, 242, 0.6)',
    'rgba(179, 226, 205, 0.6)',
    'rgba(253, 205, 172, 0.6)',
    'rgba(203, 213, 232, 0.6)',
    'rgba(244, 202, 228, 0.6)',
    'rgba(230, 245, 201, 0.6)',
    'rgba(255, 242, 174, 0.6)',
    'rgba(241, 226, 204, 0.6)',
    'rgba(204, 204, 204, 0.6)',
  ];
}
